<script setup lang="ts">
import { type AnimationPlaybackControlsWithThen, useAnimate, useInView, useMotionValue, useTransform } from 'motion-v'

const timelineItems = [
  'Spotting your quiet nights...',
  'Looking at your specials...',
  'Creating post...',
]

const activeTimelineItemIndex = ref(0)

const timelineIndex = useMotionValue(0)
const roundTimelineIndex = useTransform(timelineIndex, Math.round)

const [scope, animate] = useAnimate()
const isInView = useInView(scope, {
  amount: 'all',
})

roundTimelineIndex.on('change', (latest) => {
  activeTimelineItemIndex.value = latest
})

let beforeSequence: AnimationPlaybackControlsWithThen | undefined
let afterSequence: AnimationPlaybackControlsWithThen | undefined

watch(isInView, async (inView) => {
  beforeSequence?.stop()
  afterSequence?.stop()

  timelineIndex.set(0)

  beforeSequence = animate([
    [
      '[data-id=list-group]',
      { opacity: 1, display: 'block' },
      { duration: 0 },
    ],
    [
      'img',
      { opacity: 0, scale: 0.5, skewY: -10, display: 'none' },
      { duration: 0 },
    ],
  ])

  await beforeSequence.finished

  if (!inView) return

  afterSequence = animate([
    [
      timelineIndex,
      2,
      { duration: 3 },
    ],
    [
      '[data-id=list-group]',
      { opacity: 0, display: 'none' },
    ],
    [
      'img',
      { opacity: 1, scale: 1, skewY: 0, display: 'block' },
    ],
  ])

  await afterSequence.finished
})

</script>

<template>
  <div
    ref="scope"
    class="flex h-96 items-center justify-center place-self-center"
  >
    <div
      data-id="list-group"
      class="rounded-2xl bg-white p-8 shadow ring-1 ring-neutral-200"
    >
      <header class="flex items-center gap-x-2">
        <UiIcon
          name="magnifier-star-outline"
          class="text-2xl"
        />

        <span class="text-lg font-semibold md:text-xl">AI working on</span>
      </header>

      <ul class="isolate mt-5">
        <li
          v-for="(timelineItem, timelineItemIndex) in timelineItems"
          :key="timelineItemIndex"
          class="group grid grid-cols-[min-content_1fr] gap-x-4"
          :data-id="`timeline-${timelineItemIndex}`"
          :data-state="timelineItemIndex === activeTimelineItemIndex ? 'active' : 'inactive'"
        >
          <div
            :class="[
              'relative before:relative before:inline-flex before:size-3 before:rounded-[50%] before:bg-neutral-200 before:text-xl before:font-semibold before:text-white before:transition-all after:absolute after:left-1/2 after:top-3 after:z-[-1] after:h-full after:w-[0.125rem] after:-translate-x-1/2 after:bg-gradient-to-b after:from-neutral-200 after:to-neutral-200 after:transition-all group-data-[state=active]:before:!bg-orange-500 group-data-[state=active]:before:ring-4 group-data-[state=active]:before:ring-orange-400/30',
              {
                'after:hidden': timelineItemIndex === timelineItems.length - 1,
                'before:!bg-orange-200 after:!from-orange-200 after:!to-orange-200': activeTimelineItemIndex !== timelineItemIndex && (activeTimelineItemIndex > timelineItemIndex),
                'after:!from-orange-200 after:!to-orange-500': activeTimelineItemIndex === timelineItemIndex + 1
              }
            ]"
          />

          <div
            :class="['min-w-52 py-1 text-sm leading-none transition-all group-data-[state=active]:font-bold group-data-[state=active]:text-orange-500 md:min-w-60 md:py-0 md:text-base', {
              'mb-10': Number(timelineItemIndex) !== timelineItems.length - 1
            }]"
          >
            {{ timelineItem }}
          </div>
        </li>
      </ul>
    </div>

    <img
      :src="`${S3_ASSETS_URL}/home/instagram-reel.png`"
      class="aspect-[9/16] size-full object-contain"
      draggable="false"
      :style="{
        opacity: 0,
        display: 'none'
      }"
    >
  </div>
</template>
